// Resort-specific controls (WS-49): a package's own visitor controls (settings.json `controls`, ids start with
// `x.`). One generic component draws them from the settings list, so a package adds a control as data and no engine
// code: a control that reaches ONE bool setting is a switch button, one that reaches ONE enum setting a button that
// opens the list of its values. Texts are interface keys (settings.json `strings`): the control's label, the
// setting's label, `<setting label>.<value>` per option; `icon` on the control names one of icons.js.
// A setting the map can only show after a rebuild (in v1 every `x.` setting: its `effects` patch is merged before
// the scene is built) is written to the control's first address parameter and the page reloads with it; such a
// control without an address parameter has nowhere to keep the choice, so it is not drawn.
import { html } from '../dom.js';
import { setAddress } from '../address.js';
import { icon, hasIcon } from './icons.js';
import { popup } from './popup.js';

const ROWS = ['top-left', 'top-actions', 'controls']; // the slots that take any control with an element

/** What the generic component makes of a package control: `{ kind, key, values, param }`, or null. Pure. */
export function customKind(control, registry) {
  if (!/^x\./.test(control?.id || '') || control.settings?.length !== 1) return null;
  const e = registry.entry(control.settings[0]);
  const kind = e?.type === 'bool' ? 'switch' : e?.type === 'enum' && e.values?.length ? 'choice' : null;
  if (!kind || (e.apply === 'rebuild' && !control.url?.length)) return null;
  return { kind, key: e.key, label: e.label, values: kind === 'switch' ? [true, false] : [...e.values], param: control.url?.[0] || null };
}

export function customParts(c) {
  const { T } = c;
  const parts = {};
  // a visitor's choice: live through the runtime; a rebuild value travels in the address into the reloaded page
  const choose = (k, value) => {
    const res = c.put({ [k.key]: value });
    if (res?.rebuild?.includes(k.key) && k.param) { setAddress({ [k.param]: String(value) }); location.reload(); }
  };
  for (const ctl of c.reg.controls) {
    const k = customKind(ctl, c.reg);
    if (!k) continue;
    const name = () => T(ctl.label) || T(k.label) || ctl.id, sel = `[data-control="${ctl.id}"]`, pic = icon(hasIcon(ctl.icon) ? ctl.icon : k.kind === 'switch' ? 'check' : 'menu');
    parts[ctl.id] = k.kind === 'switch' ? {
      custom: true,
      html: () => html`<button class="round-btn toggle${c.v(k.key) === true ? ' on' : ''}" data-control="${ctl.id}" role="switch" aria-checked="${String(c.v(k.key) === true)}" title="${name()}">${pic}</button>`,
      wire(root) { const b = root.querySelector(sel); if (b) b.onclick = () => { const on = c.v(k.key) !== true; b.classList.toggle('on', on); b.setAttribute('aria-checked', String(on)); choose(k, on); }; },
    } : {
      custom: true,
      html: () => html`<button class="round-btn" data-control="${ctl.id}" title="${name()}" aria-haspopup="dialog" aria-expanded="false">${pic}</button>`,
      wire(root) {
        const b = root.querySelector(sel); if (!b) return;
        const p = popup(`pop-${ctl.id.replace(/[^a-z0-9-]/gi, '-')}`, b, 'trees-pop menu-pop', name());
        p.onOpen = () => {
          p.pop.innerHTML = html`<div class="tp-head"><span class="eyebrow">${name()}</span></div>
            ${k.values.map((v) => html`<button class="pop-item${c.v(k.key) === v ? ' on' : ''}" role="radio" aria-checked="${String(c.v(k.key) === v)}" data-value="${v}"><b>${T(`${k.label}.${v}`) || v}</b></button>`)}`;
          p.pop.querySelectorAll('[data-value]').forEach((o) => (o.onclick = () => { p.set(false); choose(k, o.dataset.value); }));
        };
      },
    };
  }
  return {
    parts,
    /** Puts each shown package control the layout does not place into its own slot (`slot` of the control, a row). */
    place(slots, shown) {
      const placed = new Set(Object.values(slots).flat());
      for (const ctl of c.reg.controls) if (parts[ctl.id] && shown.includes(ctl.id) && !placed.has(ctl.id) && ROWS.includes(ctl.slot)) (slots[ctl.slot] ||= []).push(ctl.id);
      return slots;
    },
  };
}
